import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { Brain, BookOpen, FileText, TrendingUp, Trash2, Plus, ChevronDown, ChevronRight, Sparkles } from 'lucide-react'

interface StyleProfile {
  id: string
  note_type: string
  procedure_type: string | null
  style_rules: Record<string, any>
  phrasebook: Record<string, string>
  composite_template: string | null
  composite_note_count: number
  example_note_ids: string[]
  updated_at: string | null
}

interface LearningProgress {
  notes_approved: number
  total_diffs: number
  processed_diffs: number
  style_profiles: Array<{
    note_type: string
    procedure_type: string | null
    style_rules_count: number
    phrasebook_entries: number
    has_composite_template: boolean
    example_notes: number
    composite_note_count: number
  }>
}

interface EditPattern {
  total_diffs: number
  by_category: Record<string, { count: number; examples: Array<{ section: string; before: string; after: string; created_at: string }> }>
}

export function LearningPage() {
  const [progress, setProgress] = useState<LearningProgress | null>(null)
  const [profiles, setProfiles] = useState<StyleProfile[]>([])
  const [patterns, setPatterns] = useState<EditPattern | null>(null)
  const [phrases, setPhrases] = useState<Record<string, string>>({})
  const [expandedProfile, setExpandedProfile] = useState<string | null>(null)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [newPhraseName, setNewPhraseName] = useState('')
  const [newPhraseText, setNewPhraseText] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAll()
  }, [])

  async function loadAll() {
    setLoading(true)
    try {
      const [prog, prof, pat, phr] = await Promise.all([
        api.getLearningProgress(),
        api.getStyleProfiles(),
        api.getEditPatterns(),
        api.getPhrases(),
      ])
      setProgress(prog)
      setProfiles(prof.profiles || [])
      setPatterns(pat)
      setPhrases(phr.phrases || {})
    } catch (e) {
      console.error('Failed to load learning data', e)
    } finally {
      setLoading(false)
    }
  }

  async function handleAddPhrase() {
    if (!newPhraseName.trim() || !newPhraseText.trim()) return
    await api.savePhrase(newPhraseName.trim(), newPhraseText.trim())
    setNewPhraseName('')
    setNewPhraseText('')
    const phr = await api.getPhrases()
    setPhrases(phr.phrases || {})
  }

  async function handleDeletePhrase(phraseId: string) {
    if (!window.confirm(`Delete the phrase “${phraseId}”? This can’t be undone.`)) return
    await api.deletePhrase(phraseId)
    const phr = await api.getPhrases()
    setPhrases(phr.phrases || {})
  }

  async function handleDeleteRule(profileId: string, ruleKey: string) {
    if (!window.confirm(`Delete the learned style rule “${ruleKey.replace(/_/g, ' ')}”? Dr. Scribey will stop applying it.`)) return
    await api.deleteStyleRule(profileId, ruleKey)
    const prof = await api.getStyleProfiles()
    setProfiles(prof.profiles || [])
  }

  async function handleDeleteProfile(profileId: string, label: string) {
    if (!window.confirm(`Delete the entire learned style profile for “${label}” (all rules, phrases, and the composite template)? This can’t be undone.`)) return
    await api.deleteStyleProfile(profileId)
    const prof = await api.getStyleProfiles()
    setProfiles(prof.profiles || [])
  }

  const noteTypeLabel = (t: string) => {
    const map: Record<string, string> = {
      op_note: 'Op Note', office: 'Office Visit', inpatient: 'Inpatient',
      consult: 'Consult', procedure: 'Procedure', telehealth: 'Telehealth',
    }
    return map[t] || t
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="animate-pulse text-brand-500 text-center">Loading learning data...</div>
      </div>
    )
  }

  const milestoneThresholds = [5, 10, 25, 50, 100]
  const notesApproved = progress?.notes_approved || 0
  const currentMilestone = milestoneThresholds.find(t => notesApproved < t) || 100
  const milestoneProgress = Math.min((notesApproved / currentMilestone) * 100, 100)

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Brain className="text-brand-500" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">AI Learning</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">How Dr. Scribey adapts to your style over time</p>
        </div>
      </div>

      {/* Progress Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Notes Approved" value={notesApproved} icon={<FileText size={18} />} />
        <StatCard label="Edits Tracked" value={progress?.total_diffs || 0} icon={<TrendingUp size={18} />} />
        <StatCard label="Patterns Learned" value={progress?.processed_diffs || 0} icon={<Brain size={18} />} />
        <StatCard label="Style Profiles" value={profiles.length} icon={<Sparkles size={18} />} />
      </div>

      {/* Milestone Bar */}
      <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Learning Progress
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {notesApproved} / {currentMilestone} notes to next milestone
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div
            className="bg-brand-500 h-2.5 rounded-full transition-all duration-500"
            style={{ width: `${milestoneProgress}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-[10px] text-gray-400 dark:text-gray-500">
          {milestoneThresholds.map(t => (
            <span key={t} className={notesApproved >= t ? 'text-brand-500 font-medium' : ''}>
              {t} {t === 5 ? '🌱' : t === 25 ? '🌿' : t === 50 ? '🌳' : t === 100 ? '🏆' : ''}
            </span>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
          {notesApproved < 5
            ? "Just getting started — the AI is observing your style."
            : notesApproved < 10
            ? "Building a baseline — the AI is starting to notice your patterns."
            : notesApproved < 25
            ? "Pattern recognition active — your notes are getting more personalized."
            : notesApproved < 50
            ? "Strong style profile — the AI knows your preferences well."
            : "Expert level — the AI writes like your 20-year scribe."
          }
        </p>
      </div>

      {/* Style Profiles */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <Sparkles size={18} className="text-brand-500" />
          Style Profiles
        </h2>
        {profiles.length === 0 ? (
          <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-6 text-center border border-gray-200 dark:border-gray-800">
            <p className="text-gray-500 dark:text-gray-400">No style profiles yet. Approve notes and the AI will start learning your preferences.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {profiles.map(p => {
              const isExpanded = expandedProfile === p.id
              const rulesCount = Object.keys(p.style_rules).length
              const phraseCount = Object.keys(p.phrasebook).length
              return (
                <div key={p.id} className="bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <button
                    onClick={() => setExpandedProfile(isExpanded ? null : p.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {noteTypeLabel(p.note_type)}
                        {p.procedure_type && <span className="text-gray-500 dark:text-gray-400"> — {p.procedure_type}</span>}
                      </span>
                      <div className="flex gap-2">
                        {rulesCount > 0 && (
                          <span className="text-[10px] bg-brand-500/10 text-brand-500 px-2 py-0.5 rounded-full">
                            {rulesCount} rules
                          </span>
                        )}
                        {phraseCount > 0 && (
                          <span className="text-[10px] bg-purple-500/10 text-purple-500 px-2 py-0.5 rounded-full">
                            {phraseCount} phrases
                          </span>
                        )}
                        {p.composite_template && (
                          <span className="text-[10px] bg-green-500/10 text-green-500 px-2 py-0.5 rounded-full">
                            composite template
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-4 border-t border-gray-200 dark:border-gray-700 pt-3">
                      {/* Style Rules */}
                      {rulesCount > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Learned Style Rules</h4>
                          {Object.entries(p.style_rules).map(([key, val]) => (
                            <div key={key} className="mb-2 group">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 capitalize">{key.replace(/_/g, ' ')}:</span>
                              <button
                                onClick={() => handleDeleteRule(p.id, key)}
                                className="ml-2 text-gray-400 hover:text-red-500 align-middle opacity-60 hover:opacity-100"
                                title="Delete this rule"
                              >
                                <Trash2 size={12} className="inline" />
                              </button>
                              {Array.isArray(val) ? (
                                <ul className="ml-4 mt-1 space-y-1">
                                  {(val as any[]).map((v, i) => (
                                    <li key={i} className="text-xs text-gray-600 dark:text-gray-400">
                                      {typeof v === 'object' ? (
                                        <span>"{v.from}" → "{v.to}"</span>
                                      ) : (
                                        String(v)
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : typeof val === 'object' ? (
                                <pre className="text-xs text-gray-600 dark:text-gray-400 mt-1 ml-4 whitespace-pre-wrap">
                                  {JSON.stringify(val, null, 2)}
                                </pre>
                              ) : (
                                <span className="text-xs text-gray-600 dark:text-gray-400 ml-2">{String(val)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Phrasebook */}
                      {phraseCount > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Your Phrasebook</h4>
                          <div className="space-y-2">
                            {Object.entries(p.phrasebook).map(([name, text]) => (
                              <div key={name} className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                                <div className="text-xs font-medium text-brand-500 mb-1">{name}</div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">{text}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Composite Template */}
                      {p.composite_template && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                            Composite Template ({p.composite_note_count} notes)
                          </h4>
                          <pre className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700 whitespace-pre-wrap max-h-60 overflow-y-auto">
                            {p.composite_template}
                          </pre>
                        </div>
                      )}

                      {/* Few-shot examples */}
                      {p.example_note_ids.length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Using {p.example_note_ids.length} of your approved notes as style examples
                        </div>
                      )}

                      {p.updated_at && (
                        <div className="text-[10px] text-gray-400 dark:text-gray-500">
                          Last updated: {new Date(p.updated_at).toLocaleDateString()}
                        </div>
                      )}

                      {/* Delete entire profile */}
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => handleDeleteProfile(p.id, `${noteTypeLabel(p.note_type)}${p.procedure_type ? ' — ' + p.procedure_type : ''}`)}
                          className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <Trash2 size={12} />
                          Delete this style profile
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Edit Patterns */}
      {patterns && patterns.total_diffs > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
            <TrendingUp size={18} className="text-brand-500" />
            Edit Patterns ({patterns.total_diffs} edits analyzed)
          </h2>
          <div className="space-y-2">
            {Object.entries(patterns.by_category).map(([category, data]) => {
              const isExpanded = expandedCategory === category
              return (
                <div key={category} className="bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <button
                    onClick={() => setExpandedCategory(isExpanded ? null : category)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">{category.replace(/_/g, ' ')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{data.count} edits</span>
                      {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    </div>
                  </button>
                  {isExpanded && data.examples.length > 0 && (
                    <div className="px-3 pb-3 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                      {data.examples.map((ex, i) => (
                        <div key={i} className="bg-white dark:bg-gray-900 rounded-lg p-2.5 border border-gray-200 dark:border-gray-700">
                          <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1">{ex.section}</div>
                          <div className="text-xs">
                            <span className="text-red-500 line-through">{ex.before}</span>
                            <span className="mx-1">→</span>
                            <span className="text-green-500">{ex.after}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Named Phrases */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <BookOpen size={18} className="text-brand-500" />
          Named Phrases
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Save reusable phrases. Say "use my [name] phrasing" during dictation and the AI will insert it.
        </p>

        {/* Existing Phrases */}
        {Object.keys(phrases).length > 0 ? (
          <div className="space-y-2 mb-4">
            {Object.entries(phrases).map(([name, text]) => (
              <div key={name} className="bg-gray-100 dark:bg-gray-900 rounded-xl p-3 border border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-brand-500">{name}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{text}</div>
                </div>
                <button
                  onClick={() => handleDeletePhrase(name)}
                  className="text-gray-400 hover:text-red-500 p-1 shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-4 text-center border border-gray-200 dark:border-gray-800 mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">No phrases yet. Add your first one below.</p>
          </div>
        )}

        {/* Add Phrase */}
        <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
          <div className="flex gap-2 mb-2">
            <input
              value={newPhraseName}
              onChange={e => setNewPhraseName(e.target.value)}
              placeholder="Phrase name (e.g. closure_phrasing)"
              className="flex-1 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
          <div className="flex gap-2">
            <textarea
              value={newPhraseText}
              onChange={e => setNewPhraseText(e.target.value)}
              placeholder="The actual text the AI should use..."
              rows={2}
              className="flex-1 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none"
            />
            <button
              onClick={handleAddPhrase}
              disabled={!newPhraseName.trim() || !newPhraseText.trim()}
              className="self-end px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Plus size={14} />
              Add
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

function StatCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-3 border border-gray-200 dark:border-gray-800">
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
        {icon}
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
    </div>
  )
}
